import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  computed,
  effect,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Subject, forkJoin, of, takeUntil } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { DeviceProvisioningResponse } from '../models/device-provisioning-response.model';
import { ProvisioningService } from '../services/provisioning-service/provisioning-service';
import { OperationProfileService } from '../../operation-profile/services/operation-profile.service';
import { LayoutService } from '../../layout-management/services/layout.service';
import { FileManagerService } from '../../file-manager/services/file-manager.service';
import { DeviceConfigurationService } from '../../device-configuration/services/device-configuration.service';
import { ToastService } from '../../../shared/services/toast-service';
import { OperationProfile } from '../../operation-profile/models/operation-profile.model';
import { ACTIVATION_BUTTONS, ACTIVATION_EVENTS } from '../../layout-management/models/layout.model';
import { FileItem } from '../../file-manager/models/file-manager.model';
import { WizardModal } from '../../../shared/components/wizard-modal/wizard-modal';
import { WizardStep } from '../../../shared/components/wizard-modal/wizard-step';
import { NumberInput } from '../../../shared/components/forms/number-input';
import { Icon } from '../../../shared/components/icon/icon';
import { Spinner } from '../../../shared/components/spinner/spinner';
import {
  ContentType,
  DEFAULT_OFFLINE_DELAY,
  DEFAULT_PLAYLIST_ITEM_DURATION,
  DEFAULT_SLIDESHOW_PERIOD,
  DraftComponent,
  DraftLayout,
  EditingGroup,
  BreadcrumbEntry,
  PickedFile,
  SVG_CONTENT_TYPE,
  SetupStatus,
  applyContentAssignment$,
  draftComponentFileNames,
  hydrateLayouts,
  randomId,
} from '../assign-content-shared/assign-content-shared';

@Component({
  selector: 'app-assign-content-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoDirective,
    WizardModal,
    WizardStep,
    NumberInput,
    Spinner,
    Icon,
  ],
  templateUrl: './assign-content-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignContentDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() device!: DeviceProvisioningResponse;
  @Output() close = new EventEmitter<void>();

  readonly activationEvents: string[] = [...ACTIVATION_EVENTS];
  readonly activationButtonOptions: string[] = [...ACTIVATION_BUTTONS];

  /** Drives the wizard shell's two-way `open`; closing routes through onClose(). */
  readonly open = signal(true);
  readonly isApplying = signal(false);

  // --- Setup / profile (step 1) -----------------------------------------

  readonly setupStatus = signal<SetupStatus>('loading');
  readonly availableProfiles = signal<OperationProfile[]>([]);
  readonly selectedProfileId = signal<number | null>(null);
  displayWidth = 0;
  displayHeight = 0;

  // --- Activation (step 2) / layouts --------------------------------------

  readonly layouts = signal<DraftLayout[]>([]);
  readonly isTemplateSlideshow = signal(false);
  readonly templateSlideshowChangePeriod = signal<number | null>(DEFAULT_SLIDESHOW_PERIOD);
  /** Set once the user tries to leave Activation with no layouts, so the
   *  "pick at least one" banner only shows after a real attempt. */
  readonly attemptedEventsLeave = signal(false);

  // --- Content (step 3) ---------------------------------------------------

  readonly activeLayoutId = signal<string | null>(null);
  /** Set once the user tries to leave Content with an empty layout, so the
   *  banner only shows after a real attempt. Unlike the banner text itself,
   *  this never needs to reset mid-step — `contentError` below recomputes
   *  from live layout state on every change, so fixing the last empty
   *  layout clears the banner immediately instead of only on next attempt. */
  readonly attemptedContentLeave = signal(false);
  /** Reactive, not a snapshot: derived straight from `layouts()`, so adding
   *  a component updates (or clears) this the moment it happens, rather
   *  than only the next time `contentCanLeave` runs (i.e. next Next click). */
  readonly contentError = computed<string | null>(() => {
    if (!this.attemptedContentLeave()) return null;
    const offending = this.layouts()
      .map((l, i) => ({ l, i }))
      .filter(({ l }) => l.components.length === 0);
    if (offending.length === 0) return null;
    const names = offending.map(({ i }) => this.t('steps.layoutShort', { n: i + 1 }));
    return this.t('content.componentsRequired', { names: this.joinWithAnd(names) });
  });

  /** The component currently being configured inline, for the active layout. */
  readonly editingComponent = signal<DraftComponent | null>(null);

  // File picker state (shared by the inline "new component" panel). These
  // are signals — not plain fields — because they're written from inside
  // async subscribe() callbacks; under OnPush, a plain-field write there
  // doesn't schedule change detection on its own (only a signal write, or an
  // event originating in this component's own template, does), so without
  // this the loading spinner / file grid / thumbnails would only repaint on
  // the next unrelated interaction instead of the moment the data arrives.
  readonly items = signal<FileItem[]>([]);
  readonly breadcrumb = signal<BreadcrumbEntry[]>([{ id: null, name: 'Root' }]);
  readonly page = signal(0);
  readonly pageSize = 20;
  readonly totalPages = signal(0);
  readonly isLoadingFiles = signal(false);

  /** Resolved thumbnail blob-URLs keyed by item id. Signal-owned so the
   *  template reads it purely (`getThumbnailUrl`, a plain lookup — never a
   *  write); a constructor `effect` below drives the actual fetches off
   *  `items()` instead. Writing to a signal from inside a template
   *  expression's own evaluation (as an earlier version of this did) is a
   *  reactive-context violation Angular throws on, which was silently
   *  aborting the rest of that `@for` render — the previous "only the first
   *  item or two show, thumbnails never load" bug. An empty-string value is
   *  an in-flight marker. */
  private readonly thumbnails = signal<Record<string, string>>({});

  constructor(
    private provisioningService: ProvisioningService,
    private operationProfileService: OperationProfileService,
    private layoutService: LayoutService,
    private fileManagerService: FileManagerService,
    private deviceConfigurationService: DeviceConfigurationService,
    private toastService: ToastService,
    private transloco: TranslocoService,
  ) {
    // Fetch thumbnails for the currently-listed folder's images, and revoke
    // any blob URLs no longer referenced by it (navigating away/paging).
    effect(() => {
      const items = this.items();
      const visible = new Set(items.map(i => i.id));
      const current = this.thumbnails();

      const stale = Object.keys(current).filter(id => !visible.has(id));
      if (stale.length) {
        this.thumbnails.update(map => {
          const next = { ...map };
          for (const id of stale) {
            if (next[id]) URL.revokeObjectURL(next[id]);
            delete next[id];
          }
          return next;
        });
      }

      for (const item of items) {
        if (this.isImage(item) && !(item.id in this.thumbnails())) {
          this.loadThumbnail(item);
        }
      }
    });
  }

  ngOnInit() {
    this.runSetup();
    this.hydrateExistingContent();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    Object.values(this.thumbnails()).forEach(url => url && URL.revokeObjectURL(url));
  }

  t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(`devices.assignContent.${key}`, params);
  }

  /** "1 layout" / "3 layouts" — never a bare "{{count}} layouts" at count 1. */
  private countLabel(count: number, oneKey: string, manyKey: string): string {
    return count === 1 ? this.t(oneKey) : this.t(manyKey, { count });
  }

  private joinWithAnd(items: string[]): string {
    if (items.length <= 1) return items[0] ?? '';
    return `${items.slice(0, -1).join(', ')} ${this.t('common.and')} ${items[items.length - 1]}`;
  }

  private runSetup() {
    if (!this.device.deviceConfigurationId) {
      this.setupStatus.set('noConfig');
      return;
    }

    this.deviceConfigurationService.getDeviceConfigurationById(this.device.deviceConfigurationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.displayWidth = res.data.displayWidth;
          this.displayHeight = res.data.displayHeight;
          this.loadProfiles();
        },
        error: () => this.setupStatus.set('error'),
      });
  }

  private loadProfiles() {
    this.operationProfileService.getProfiles(0, 100, 'created', 'DESC')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.availableProfiles.set(res.data);
          this.setupStatus.set(res.data.length === 0 ? 'noProfiles' : 'ok');
          // Pre-select the device's already-assigned profile, if any — the
          // device's `operationProfileId` is the profile's numeric `id`
          // (a FK column), not its human-readable `operationProfileId`
          // string field, despite the same name on both DTOs.
          const current = res.data.find((p) => p.id === this.device.operationProfileId);
          if (current) this.selectedProfileId.set(current.id);
        },
        error: () => this.setupStatus.set('error'),
      });
  }

  /** Set once the device's already-assigned layout group has been loaded, so
   *  `applyAll` edits it in place instead of creating a new one. Left `null`
   *  (the default, blank-draft behaviour) if the device has no content
   *  assigned yet, or if loading it fails — apply then just creates fresh. */
  private editingGroup: EditingGroup | null = null;

  private hydrateExistingContent(): void {
    const groupIds = this.device.layoutGroupIds ?? [];
    if (!groupIds.length) return;
    const [primaryId, ...strayGroupIds] = groupIds;

    forkJoin({
      layouts: this.layoutService.getLayoutsForGroup(primaryId),
      groups: this.layoutService.getLayoutGroups(0, 100),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ layouts, groups }) => {
          const group = groups.data.find((g) => g.id === primaryId);
          if (!group) return;
          const existingLayouts = layouts.data ?? [];
          this.layouts.set(hydrateLayouts(existingLayouts));
          this.isTemplateSlideshow.set(!!group.isStateSlideshow);
          this.templateSlideshowChangePeriod.set(group.stateSlideshowChangePeriod ?? DEFAULT_SLIDESHOW_PERIOD);
          this.editingGroup = {
            id: group.id,
            groupIdString: group.groupId,
            originalLayoutIds: existingLayouts.map((l) => l.id),
            strayGroupIds,
          };
        },
        // Non-fatal: falls back to the blank-draft, create-new behaviour.
        error: () => {},
      });
  }

  get selectedProfile(): OperationProfile | null {
    const id = this.selectedProfileId();
    return this.availableProfiles().find(p => p.id === id) ?? null;
  }

  /** Gate for the setup step: setup succeeded and a profile is chosen. */
  readonly setupCanLeave = () => this.setupStatus() === 'ok' && this.selectedProfile !== null;

  // --- Rail sublabels ------------------------------------------------

  setupSublabel(): string {
    return this.selectedProfile?.operationProfileId ?? '';
  }

  eventsSublabel(): string {
    const labels = this.activationEvents
      .filter(e => this.layoutsForEvent(e).length > 0)
      .map(e => this.t(`events.${e}.label`));
    return labels.join(', ');
  }

  contentSublabel(): string {
    const layouts = this.layouts();
    const components = layouts.reduce((n, l) => n + l.components.length, 0);
    return `${this.countLabel(layouts.length, 'counts.layoutOne', 'counts.layoutMany')} · ` +
      `${this.countLabel(components, 'counts.componentOne', 'counts.componentMany')}`;
  }

  reviewSublabel(): string {
    return this.countLabel(this.layouts().length, 'counts.layoutOne', 'counts.layoutMany');
  }

  // --- Activation: events → layouts ---------------------------------------

  layoutsForEvent(event: string): DraftLayout[] {
    return this.layouts().filter(l => l.activationEvent === event);
  }

  isEventOn(event: string): boolean {
    return this.layoutsForEvent(event).length > 0;
  }

  layoutIndex(id: string): number {
    return this.layouts().findIndex(l => l.id === id);
  }

  /** Every button not currently claimed by a BUTTON_EVENT layout. */
  freeButtons(): string[] {
    const used = new Set(this.layoutsForEvent('BUTTON_EVENT').map(l => l.activationButton));
    return this.activationButtonOptions.filter(b => !used.has(b));
  }

  buttonLayout(button: string): DraftLayout | null {
    return this.layoutsForEvent('BUTTON_EVENT').find(l => l.activationButton === button) ?? null;
  }

  /** Toggles a whole event card on/off. DEFAULT and BUTTON_EVENT have their
   *  own multi-layout shape; every other event maps to exactly one layout. */
  toggleEvent(event: string, on: boolean): void {
    if (event === 'DEFAULT') {
      if (on) this.incrementDefault();
      else {
        this.layouts.update(list => list.filter(l => l.activationEvent !== 'DEFAULT'));
        this.isTemplateSlideshow.set(false);
      }
      return;
    }
    if (event === 'BUTTON_EVENT') {
      if (on) {
        const free = this.freeButtons();
        if (free.length > 0) this.addLayout(event, { activationButton: free[0] });
      } else {
        this.layouts.update(list => list.filter(l => l.activationEvent !== 'BUTTON_EVENT'));
      }
      return;
    }
    if (on) {
      if (!this.isEventOn(event)) this.addLayout(event);
    } else {
      this.layouts.update(list => list.filter(l => l.activationEvent !== event));
    }
  }

  toggleButtonTile(button: string): void {
    const existing = this.buttonLayout(button);
    if (existing) {
      this.layouts.update(list => list.filter(l => l.id !== existing.id));
    } else {
      this.addLayout('BUTTON_EVENT', { activationButton: button });
    }
  }

  defaultCount(): number {
    return this.layoutsForEvent('DEFAULT').length;
  }

  incrementDefault(): void {
    this.addLayout('DEFAULT');
    if (this.defaultCount() > 1) this.isTemplateSlideshow.set(true);
  }

  decrementDefault(): void {
    const defaults = this.layoutsForEvent('DEFAULT');
    if (defaults.length <= 1) return;
    const last = defaults[defaults.length - 1];
    this.layouts.update(list => list.filter(l => l.id !== last.id));
    if (this.defaultCount() <= 1) this.isTemplateSlideshow.set(false);
  }

  offlineLayout(): DraftLayout | null {
    return this.layoutsForEvent('OFFLINE_MODE')[0] ?? null;
  }

  setOfflineDelay(seconds: number | null): void {
    const layout = this.offlineLayout();
    if (!layout) return;
    layout.activationOfflineDelay = seconds;
    this.layouts.update(list => [...list]);
  }

  setSlideshowPeriod(seconds: number | null): void {
    this.templateSlideshowChangePeriod.set(seconds);
  }

  private addLayout(event: string, extra: Partial<Pick<DraftLayout, 'activationButton'>> = {}): void {
    const layout: DraftLayout = {
      id: randomId(),
      originalId: null,
      activationEvent: event,
      activationButton: extra.activationButton ?? null,
      activationOfflineDelay: event === 'OFFLINE_MODE' ? DEFAULT_OFFLINE_DELAY : null,
      components: [],
    };
    this.layouts.update(list => [...list, layout]);
  }

  /** Gate for the Activation step: at least one event picked. */
  eventsCanLeave = () => {
    const ok = this.layouts().length > 0;
    this.attemptedEventsLeave.set(!ok);
    return ok;
  };

  eventsError(): string | null {
    return this.attemptedEventsLeave() && this.layouts().length === 0
      ? this.t('events.pickAtLeastOneError')
      : null;
  }

  eventsHint(): string {
    const count = this.layouts().length;
    return count === 0
      ? this.t('events.pickAtLeastOne')
      : this.t('events.layoutsToBuild', { label: this.countLabel(count, 'counts.layoutOne', 'counts.layoutMany') });
  }

  // --- Content: active layout tab -----------------------------------------

  /** Self-healing "active" id — falls back to the first layout if the
   *  previously active one was removed on Activation. */
  effectiveActiveLayoutId(): string | null {
    const id = this.activeLayoutId();
    if (id && this.layouts().some(l => l.id === id)) return id;
    return this.layouts()[0]?.id ?? null;
  }

  activeLayout(): DraftLayout | null {
    const id = this.effectiveActiveLayoutId();
    return this.layouts().find(l => l.id === id) ?? null;
  }

  selectTab(id: string): void {
    this.activeLayoutId.set(id);
  }

  layoutTabLabel(layout: DraftLayout, index: number): string {
    return this.t('steps.layoutShort', { n: index + 1 });
  }

  layoutEventDetail(layout: DraftLayout): string {
    if (layout.activationEvent === 'BUTTON_EVENT') {
      return this.t('content.buttonDetail', { button: layout.activationButton });
    }
    return this.t(`events.${layout.activationEvent}.label`);
  }

  layoutComponentTypesLine(layout: DraftLayout): string {
    return layout.components.map(c => this.t(`componentType.${c.contentType}`)).join(', ');
  }

  componentFileNames(component: DraftComponent): string {
    return draftComponentFileNames(component).join(', ');
  }

  componentFileCount(component: DraftComponent): number {
    return component.files.length || draftComponentFileNames(component).length;
  }

  /** Gate for the Content step: no layout may hold zero components. Jumps
   *  the active tab to the first offender; the banner itself is the
   *  `contentError` computed above, which reacts to `layouts()` directly. */
  contentCanLeave = () => {
    const offending = this.layouts().find(l => l.components.length === 0);
    this.attemptedContentLeave.set(!!offending);
    if (!offending) return true;
    this.activeLayoutId.set(offending.id);
    return false;
  };

  // --- Components within the active layout --------------------------------

  rectStyle(c: { left: number; top: number; right: number; bottom: number }) {
    const w = Math.max(this.displayWidth, 1);
    const h = Math.max(this.displayHeight, 1);
    return {
      left: `${(c.left / w) * 100}%`,
      top: `${(c.top / h) * 100}%`,
      width: `${Math.max(((c.right - c.left) / w) * 100, 0)}%`,
      height: `${Math.max(((c.bottom - c.top) / h) * 100, 0)}%`,
    };
  }

  removeComponent(layout: DraftLayout, component: DraftComponent) {
    layout.components = layout.components.filter(c => c.id !== component.id);
    this.layouts.update(list => [...list]);
  }

  startAddComponent() {
    this.editingComponent.set({
      id: randomId(),
      left: 0,
      top: 0,
      right: this.displayWidth,
      bottom: this.displayHeight,
      contentType: 'IMAGE',
      files: [],
    });
    this.breadcrumb.set([{ id: null, name: 'Root' }]);
    this.page.set(0);
    this.fetchFiles();
  }

  setComponentType(type: ContentType) {
    const c = this.editingComponent();
    if (!c) return;
    // Switching type clears picked files — they no longer apply.
    this.editingComponent.set({ ...c, contentType: type, files: [] });
  }

  clampComponentRect() {
    const c = this.editingComponent();
    if (!c) return;
    c.left = Math.max(0, Math.min(c.left, this.displayWidth));
    c.right = Math.max(0, Math.min(c.right, this.displayWidth));
    c.top = Math.max(0, Math.min(c.top, this.displayHeight));
    c.bottom = Math.max(0, Math.min(c.bottom, this.displayHeight));
  }

  componentRectValid(): boolean {
    const c = this.editingComponent();
    return !!c && c.right > c.left && c.bottom > c.top;
  }

  finishAddComponent() {
    const layout = this.activeLayout();
    const component = this.editingComponent();
    if (!layout || !component || component.files.length === 0 || !this.componentRectValid()) return;
    layout.components = [...layout.components, component];
    this.layouts.update(list => [...list]);
    this.cancelAddComponent();
  }

  cancelAddComponent() {
    this.editingComponent.set(null);
  }

  // --- File picker (used within the inline "new component" panel) --------

  fetchFiles() {
    this.isLoadingFiles.set(true);
    const id = this.breadcrumb()[this.breadcrumb().length - 1].id;
    const obs = id === null
      ? this.fileManagerService.getRootContents(this.page(), this.pageSize)
      : this.fileManagerService.getDirectoryContents(id, this.page(), this.pageSize);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.items.set(response.data);
        this.totalPages.set(response.totalPages);
        this.isLoadingFiles.set(false);
      },
      error: () => {
        this.toastService.show(this.t('toast.loadFilesFailed'), 'error');
        this.isLoadingFiles.set(false);
      },
    });
  }

  navigateInto(item: FileItem) {
    this.breadcrumb.update(list => [...list, { id: item.id, name: item.name }]);
    this.page.set(0);
    this.fetchFiles();
  }

  navigateTo(index: number) {
    this.breadcrumb.update(list => list.slice(0, index + 1));
    this.page.set(0);
    this.fetchFiles();
  }

  onItemClick(item: FileItem) {
    if (item.type === 'FOLDER') {
      this.navigateInto(item);
      return;
    }
    const c = this.editingComponent();
    if (!c || !this.isSelectable(item)) return;

    if (c.contentType === 'SVG') {
      this.editingComponent.set({ ...c, files: [{ file: item, duration: DEFAULT_PLAYLIST_ITEM_DURATION }] });
    } else {
      this.toggleFileSelection(item);
    }
  }

  toggleFileSelection(item: FileItem) {
    const c = this.editingComponent();
    if (!c) return;
    const index = c.files.findIndex(f => f.file.id === item.id);
    const files = index === -1
      ? [...c.files, { file: item, duration: DEFAULT_PLAYLIST_ITEM_DURATION }]
      : c.files.filter(f => f.file.id !== item.id);
    this.editingComponent.set({ ...c, files });
  }

  isPicked(item: FileItem): boolean {
    return !!this.editingComponent()?.files.some(f => f.file.id === item.id);
  }

  removePickedFile(item: FileItem) {
    const c = this.editingComponent();
    if (!c) return;
    this.editingComponent.set({ ...c, files: c.files.filter(f => f.file.id !== item.id) });
  }

  movePickedFile(index: number, delta: number) {
    const c = this.editingComponent();
    if (!c) return;
    const target = index + delta;
    if (target < 0 || target >= c.files.length) return;
    const files = [...c.files];
    [files[index], files[target]] = [files[target], files[index]];
    this.editingComponent.set({ ...c, files });
  }

  setPickedFileDuration(index: number, seconds: number | null) {
    const c = this.editingComponent();
    if (!c) return;
    const files = c.files.map((f, i) => (i === index ? { ...f, duration: seconds ?? DEFAULT_PLAYLIST_ITEM_DURATION } : f));
    this.editingComponent.set({ ...c, files });
  }

  prevPage() {
    this.page.update(p => p - 1);
    this.fetchFiles();
  }

  nextPage() {
    this.page.update(p => p + 1);
    this.fetchFiles();
  }

  isImage(item: FileItem): boolean {
    return !!item.contentType?.startsWith('image/') && item.contentType !== SVG_CONTENT_TYPE;
  }

  isSvg(item: FileItem): boolean {
    return item.contentType === SVG_CONTENT_TYPE;
  }

  isSelectable(item: FileItem): boolean {
    return this.editingComponent()?.contentType === 'SVG' ? this.isSvg(item) : this.isImage(item);
  }

  /** Pure read of a resolved thumbnail URL (empty while pending/absent) —
   *  the effect in the constructor is what actually fetches these. */
  getThumbnailUrl(item: FileItem): string {
    return this.thumbnails()[item.id] ?? '';
  }

  /** Fetch one thumbnail and fold the resulting blob URL into the signal map. */
  private loadThumbnail(item: FileItem): void {
    // In-flight marker so the effect doesn't re-dispatch on the next run.
    this.thumbnails.update(map => ({ ...map, [item.id]: '' }));
    this.fileManagerService.downloadThumbnail(item.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(blob => {
        const url = URL.createObjectURL(blob);
        // Guard against a race: the item may have been evicted (folder
        // navigated away from) while the request was in flight.
        if (!(item.id in this.thumbnails())) {
          URL.revokeObjectURL(url);
          return;
        }
        this.thumbnails.update(map => ({ ...map, [item.id]: url }));
      });
  }

  // --- Apply ---------------------------------------------------------

  onFinished(wizard: WizardModal): void {
    if (this.layouts().length === 0) {
      wizard.goTo('events');
      return;
    }
    // Reuses the Content step's own gate so the banner + active-tab jump
    // happen the same way whether the user got here via Next or a rail jump.
    if (!this.contentCanLeave()) {
      wizard.goTo('content');
      return;
    }
    this.applyAll();
  }

  private applyAll(): void {
    const profile = this.selectedProfile;
    const layouts = this.layouts();
    if (layouts.length === 0 || !profile) return;
    this.isApplying.set(true);

    applyContentAssignment$(
      {
        clientId: this.device.clientId,
        profileId: profile.id,
        layouts,
        isTemplateSlideshow: this.isTemplateSlideshow(),
        templateSlideshowChangePeriod: this.templateSlideshowChangePeriod(),
        editing: this.editingGroup,
      },
      {
        layoutService: this.layoutService,
        provisioningService: this.provisioningService,
        fileManagerService: this.fileManagerService,
      },
    ).pipe(
      catchError(() => {
        this.toastService.show(this.t('toast.applyFailed'), 'error');
        this.isApplying.set(false);
        return of(null);
      }),
      takeUntil(this.destroy$),
    ).subscribe((result) => {
      this.isApplying.set(false);
      if (result === null) return;
      this.toastService.show(this.t('toast.applySuccess', { count: layouts.length }), 'success');
      this.open.set(false);
      this.close.emit();
    });
  }

  onClose(): void {
    this.close.emit();
  }
}
